import React from 'react'

import { Icon } from '@clientbase/clientbase-library'
import { Box, Tooltip } from '@mui/material'
import { InvoiceOpenIndicator } from 'features/invoice-timeline'
import InvoiceIcon from 'templates/Invoice/components/InvoiceIcon'
import { Record as ChargeRecord } from 'templates/v2/Charge/components/ChargeTable/ChargeTable'
import {
  getStatusOptionCharge,
  getCustomerStageLabel
} from 'templates/v2/Charge/components/ChargeTable/ChargeTable.mapper'
import * as S from 'templates/v2/Charge/components/ChargeTable/ChargeTable.styles'

import { ButtonRounded } from 'components/button'
import Can from 'components/Can'
import InfoHint from 'components/InfoHint'
import CustomerAvatar from 'components/v2/CustomerAvatar'

import { getInvoiceUrl } from 'utils/domain-urls'
import formatCpfCnpj from 'utils/FormatCpfCnpj'
import { formatDate } from 'utils/FormatDate'
import { formatMoney } from 'utils/FormatMoney'

import { theme } from 'styles/theme'

import {
  BooleanChip,
  CellChip,
  ChipList,
  customerCategoryTone,
  filterPaymentTypes,
  getPaidPaymentLabel,
  getPaidPayments,
  getPaymentTypeLabel,
  merchantColumn,
  normalizeTagColor
} from '../cells'
import type { ColumnDef } from '../types'

// Internal helper for NFS-e status tooltip (mirrors getStatusOption in mapper)

const getNfseStatusOption = (status: string, nfsePolicy?: string) => {
  switch (status) {
    case 'scheduled':
      if (nfsePolicy === 'on_billing_issued') {
        return {
          label: 'Será emitida quando a fatura abrir para pagamento',
          color: theme.palette.alert[300]
        }
      }
      if (nfsePolicy === 'on_billing_paid') {
        return {
          label: 'Será emitida quando paga',
          color: theme.palette.alert[300]
        }
      }
      return {
        label: 'Programada para emissão',
        color: theme.palette.alert[300]
      }
    case 'issued':
      return { label: 'Emitida', color: theme.palette.primary.main }
    case 'not_scheduled':
      return {
        label: 'Não está programada',
        color: theme.palette.neutral[500]
      }
    case 'failed':
      return { label: 'Erro ao emitir', color: theme.palette.error[300] }
    default:
      return {
        label: 'Status desconhecido',
        color: theme.palette.neutral[300]
      }
  }
}

export const catalog: ColumnDef<ChargeRecord>[] = [
  // ── EXISTING COLUMNS (defaultVisible: true) ──────────────────────────────

  // SC-10999: "Empresa", só na visão multi-empresas
  merchantColumn<ChargeRecord>(),

  {
    id: 'customer',
    label: 'Cliente',
    defaultVisible: true,
    defaultOrder: 1,
    locked: true,
    lockedPosition: 'start',
    sortable: { sortField: 'customer_name' },
    cell: (record, ctx) => {
      const showCustomerStage = !!(ctx.baseFilters as any)?.customer_category
      return (
        <Box display="flex" alignItems="center" gap="11px">
          <CustomerAvatar
            name={record?.customer?.name}
            logoUrl={(record?.customer as any)?.logo_url}
          />
          <p>{record?.customer?.name}</p>
          <span style={{ color: '#777877', fontSize: '0.857em' }}>
            {record?.customer?.nickname}
          </span>
          {showCustomerStage && record?.customer?.category && (
            <S.CustomerStage stage={record.customer.category}>
              {getCustomerStageLabel(record.customer.category)}
            </S.CustomerStage>
          )}
        </Box>
      )
    }
  },

  {
    id: 'due_date',
    label: 'Vencimento',
    defaultVisible: true,
    defaultOrder: 2,
    sortable: { sortField: 'due_date' },
    cell: (record) => <p>{formatDate(record?.due_date)}</p>
  },

  {
    id: 'date_paid',
    label: 'Pagamento',
    defaultVisible: true,
    defaultOrder: 3,
    sortable: { sortField: 'date_paid' },
    cell: (record) => <p>{formatDate(record?.date_paid || '')}</p>
  },

  {
    id: 'amount_billed',
    label: 'Valor cobrado',
    defaultVisible: true,
    defaultOrder: 4,
    sortable: { sortField: 'amount_billed' },
    cell: (record) => <p>{formatMoney(record?.amount_billed)}</p>
  },

  {
    id: 'amount_paid',
    label: 'Valor Pago',
    defaultVisible: true,
    defaultOrder: 5,
    sortable: { sortField: 'amount_paid' },
    cell: (record) => (
      <p>
        {record?.amount_paid != '0.0' ? formatMoney(record?.amount_paid) : ''}
      </p>
    )
  },

  {
    id: 'status',
    label: 'Status',
    defaultVisible: true,
    defaultOrder: 6,
    sortable: { sortField: 'status' },
    header: () => (
      <Box display="flex" gap="4px" alignItems="center">
        Status
        <InfoHint
          title={
            <Box>
              <p>
                <b>Pendente: </b>A cobrança está agendada para emissão.
              </p>
              <p>
                <b>Aberta: </b>A cobrança está em aberto e aguarda pagamento.
              </p>
              <p>
                <b>Atrasada: </b>A cobrança está em aberto e a data de
                vencimento já passou.
              </p>
              <p>
                <b>Paga: </b>A cobrança foi paga.
              </p>
              <p>
                <b>Expirada: </b>A cobrança não foi paga dentro do prazo máximo.
              </p>
              <p>
                <b>Cancelada: </b>A cobrança foi cancelada.
              </p>
            </Box>
          }
        />
      </Box>
    ),
    cell: (record) => (
      <S.Status status={record?.status} dueDate={record.due_date}>
        {getStatusOptionCharge(record?.status, record?.due_date).label}
      </S.Status>
    )
  },

  // ── LOCKED: ACTIONS ──────────────────────────────────────────────────────

  {
    id: 'actions',
    label: 'Ações',
    defaultVisible: true,
    defaultOrder: 7,
    locked: true,
    lockedPosition: 'end',
    cell: (record, ctx) => {
      const toggleModals = ctx.toggleModals
      const notViewActions = (ctx as any).notViewActions
      const isScheduledNfseClickable =
        record?.nfse_status === 'scheduled' && !!record?.nfse_scheduled?.uuid
      const isIssuedNfseClickable =
        !!record?.nfse?.uuid &&
        record?.nfse_status !== 'not_scheduled' &&
        record?.nfse_status !== 'scheduled'
      const isNfseClickable = isScheduledNfseClickable || isIssuedNfseClickable
      return (
        <Box
          display="flex"
          justifyContent="flex-end"
          width="100%"
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        >
          {!notViewActions && (
            <>
              <Can permissions={['nfses:show', 'customers:view_financial']}>
                <Tooltip
                  enterTouchDelay={0}
                  arrow={true}
                  placement="top"
                  title={
                    getNfseStatusOption(
                      record?.nfse_status,
                      record?.nfse_policy
                    ).label
                  }
                >
                  <Box
                    sx={{
                      cursor: isNfseClickable ? 'pointer' : 'default'
                    }}
                    onClick={() => {
                      if (!toggleModals) return
                      if (isScheduledNfseClickable) {
                        toggleModals({
                          modal: 'isOpenDetailInvoice',
                          nfseScheduledUuid: record.nfse_scheduled?.uuid
                        })
                      } else if (isIssuedNfseClickable) {
                        toggleModals({
                          modal: 'isOpenDetailInvoice',
                          uuid: record.nfse.uuid
                        })
                      }
                    }}
                  >
                    <ButtonRounded
                      variant="transparent"
                      disabled={!isNfseClickable}
                    >
                      <InvoiceIcon
                        color={
                          getNfseStatusOption(
                            record?.nfse_status,
                            record?.nfse_policy
                          ).color
                        }
                      />
                    </ButtonRounded>
                  </Box>
                </Tooltip>
              </Can>

              <Can
                permissions={['recurrences:show', 'customers:view_financial']}
              >
                <Tooltip
                  arrow={true}
                  placement="top"
                  title={
                    !record?.recurrence_uuid
                      ? 'Cobrança única'
                      : 'Visualizar recorrência'
                  }
                >
                  <Box
                    sx={{
                      cursor: !record?.recurrence_uuid ? 'default' : 'pointer'
                    }}
                  >
                    <ButtonRounded
                      disabled={!record?.recurrence_uuid}
                      onClick={() =>
                        toggleModals &&
                        toggleModals({
                          modal: 'isOpenModalRecurrenceDetail',
                          uuid: record.recurrence_uuid
                        })
                      }
                      variant="transparent"
                    >
                      <Icon
                        icon="cached"
                        sx={{
                          color:
                            theme.palette.neutral[
                              !record?.recurrence_uuid ? 700 : 200
                            ]
                        }}
                      />
                    </ButtonRounded>
                  </Box>
                </Tooltip>
              </Can>

              <Can permissions={['billings:show', 'customers:view_financial']}>
                <Tooltip
                  enterTouchDelay={0}
                  arrow={true}
                  placement="top"
                  title="Visualizar fatura"
                >
                  <Box>
                    <ButtonRounded
                      disabled={
                        getStatusOptionCharge(record.status || '').disabled
                      }
                      onClick={() => {
                        window.open(getInvoiceUrl(record.uuid), '_blank')
                      }}
                      variant="transparent"
                    >
                      <Icon
                        icon="receipt"
                        sx={{
                          color: getStatusOptionCharge(record.status || '')
                            .disabled
                            ? theme.palette.neutral[700]
                            : theme.palette.neutral[200]
                        }}
                      />
                    </ButtonRounded>
                  </Box>
                </Tooltip>
              </Can>

              <Can permissions={['billings:show', 'customers:view_financial']}>
                {/* SC-10890: o ícone era só tooltip (disabled). Agora abre o
                    detalhe da cobrança, onde a seção "Histórico da cobrança"
                    mostra a timeline — em vez de um modal separado, que
                    duplicaria a mesma timeline em duas superfícies. */}
                <InvoiceOpenIndicator
                  variant="row"
                  viewed={record.viewed}
                  viewsCount={record.open_count}
                  lastViewedAt={record.last_opened_at}
                  onClick={() =>
                    toggleModals?.({
                      modal: 'isOpenModalChargeDetail',
                      uuid: record.uuid
                    })
                  }
                />
              </Can>

              <Can permissions={['billings:show', 'customers:view_financial']}>
                <Tooltip
                  enterTouchDelay={0}
                  arrow={true}
                  placement="top"
                  title="Detalhes cobrança"
                >
                  <Box>
                    <ButtonRounded
                      onClick={() =>
                        toggleModals &&
                        toggleModals({
                          modal: 'isOpenModalChargeDetail',
                          uuid: record.uuid
                        })
                      }
                      variant="transparent"
                      data-cy="open-billing"
                    >
                      <Icon
                        icon="settings"
                        sx={{ color: theme.palette.neutral[200] }}
                      />
                    </ButtonRounded>
                  </Box>
                </Tooltip>
              </Can>
            </>
          )}
        </Box>
      )
    }
  },

  // ── NEW COLUMNS (defaultVisible: false) ──────────────────────────────────

  {
    id: 'amount_updated',
    label: 'Valor atualizado',
    defaultVisible: false,
    defaultOrder: 8,
    cell: (record) => <p>{formatMoney((record as any)?.amount_updated)}</p>
  },

  {
    id: 'payment_type',
    label: 'Meio de pagamento',
    // Ligada por padrao: numa cobranca paga, por onde o dinheiro entrou e uma
    // das primeiras coisas que se procura, e no mobile o card e a unica
    // visao — o seletor de colunas nao esta a mao ali. `defaultOrder: 9` a
    // coloca depois de Valor Pago, no fim das linhas do card.
    //
    // Quem ja desligou a coluna no seletor continua sem ela: a preferencia
    // guarda so o que o usuario mexeu, e `defaultVisible` e o fallback de
    // quem nunca tocou nesta.
    defaultVisible: true,
    defaultOrder: 9,
    cell: (record) => {
      // Numa cobrança paga a pergunta "meio de pagamento" só tem uma resposta
      // útil: por onde ela foi paga. `billing.payment_type` responde outra
      // coisa — a lista do que a cobrança ACEITA — e numa cobrança liquidada
      // isso vira ruído (uma cobrança paga aparecia como
      // "Boleto · Pix · Cartão · Cartão recorrente"). Enquanto ela não é paga
      // não há realizado, e aí a lista de aceitos é a resposta certa.
      const paid = getPaidPayments(record?.payments)
      if (paid.length) {
        return (
          <ChipList>
            {paid.map((payment, index) => (
              <CellChip
                key={`${payment.payment_type}-${index}`}
                tone="neutral"
                label={getPaidPaymentLabel(payment)}
              />
            ))}
          </ChipList>
        )
      }

      const raw: string = (record as any)?.payment_type || ''
      if (!raw) return null
      const parts = filterPaymentTypes(raw.split(';').filter(Boolean))
      return (
        <ChipList>
          {parts.map((pt) => (
            <CellChip key={pt} tone="neutral" label={getPaymentTypeLabel(pt)} />
          ))}
        </ChipList>
      )
    }
  },

  {
    id: 'competence_date',
    label: 'Competência',
    defaultVisible: false,
    defaultOrder: 10,
    cell: (record) => (
      <p>{formatDate((record as any)?.competence_date || '')}</p>
    )
  },

  {
    id: 'description',
    label: 'Descrição',
    defaultVisible: false,
    defaultOrder: 11,
    cell: (record) => {
      const desc: string = (record as any)?.description || ''
      if (!desc) return null
      const truncated = desc.length > 50 ? desc.slice(0, 50) + '…' : desc
      return (
        <Tooltip title={desc.length > 50 ? desc : ''} placement="top">
          <span style={{ cursor: desc.length > 50 ? 'help' : 'default' }}>
            {truncated}
          </span>
        </Tooltip>
      )
    }
  },

  {
    id: 'customer_category',
    label: 'Categoria do cliente',
    defaultVisible: false,
    defaultOrder: 12,
    cell: (record) => {
      const category = record?.customer?.category
      if (!category) return null
      return (
        <CellChip
          tone={customerCategoryTone(category)}
          label={getCustomerStageLabel(category)}
        />
      )
    }
  },

  {
    id: 'tags',
    label: 'Tags',
    defaultVisible: false,
    defaultOrder: 13,
    cell: (record) => {
      const tags: Array<{ name: string; color?: string }> =
        (record as any)?.tags || []
      if (!tags.length) return null
      return (
        <ChipList>
          {tags.map((tag, i) => {
            const color = normalizeTagColor(tag.color)
            return color ? (
              <CellChip
                key={i}
                tone="custom"
                color={theme.palette.neutral[200]}
                background={color}
                label={tag.name}
              />
            ) : (
              <CellChip key={i} tone="neutral" label={tag.name} />
            )
          })}
        </ChipList>
      )
    }
  },

  {
    id: 'recurrence_cycle',
    label: 'Ciclo recorrência',
    defaultVisible: false,
    defaultOrder: 15,
    cell: (record) => {
      const cycle: number | undefined = (record as any)?.recurrence_cycle
      if (!record?.recurrence_uuid) return <p>Avulsa</p>
      return <p>{cycle != null ? String(cycle) : 'Avulsa'}</p>
    }
  },

  {
    id: 'issue_date',
    label: 'Emitida em',
    defaultVisible: false,
    defaultOrder: 16,
    cell: (record) => <p>{formatDate((record as any)?.issue_date || '')}</p>
  },

  {
    id: 'created_at',
    label: 'Criada em',
    defaultVisible: false,
    defaultOrder: 17,
    cell: (record) => <p>{formatDate((record as any)?.created_at || '')}</p>
  },

  {
    id: 'charged_fine',
    label: 'Multa cobrada',
    defaultVisible: false,
    defaultOrder: 18,
    cell: (record) => {
      const val = (record as any)?.charged_fine
      if (!val || val === '0.0' || val === '0') return null
      return <p>{formatMoney(val)}</p>
    }
  },

  {
    id: 'charged_interest',
    label: 'Juros cobrados',
    defaultVisible: false,
    defaultOrder: 19,
    cell: (record) => {
      const val = (record as any)?.charged_interest
      if (!val || val === '0.0' || val === '0') return null
      return <p>{formatMoney(val)}</p>
    }
  },

  {
    id: 'antecipated',
    label: 'Antecipada',
    defaultVisible: false,
    defaultOrder: 20,
    cell: (record) => <BooleanChip value={(record as any)?.antecipated} />
  },

  {
    id: 'customer_document',
    label: 'Documento do cliente',
    defaultVisible: false,
    defaultOrder: 21,
    cell: (record) => {
      const doc = (record?.customer as any)?.document
      if (!doc) return null
      return <p>{formatCpfCnpj(doc)}</p>
    }
  },

  {
    id: 'customer_email',
    label: 'Email do cliente',
    defaultVisible: false,
    defaultOrder: 22,
    cell: (record) => {
      const email = (record?.customer as any)?.email
      if (!email) return null
      return <p>{email}</p>
    }
  },

  {
    id: 'customer_phone',
    label: 'Telefone do cliente',
    defaultVisible: false,
    defaultOrder: 23,
    cell: (record) => {
      const phone = (record?.customer as any)?.phone
      if (!phone) return null
      return <p>{phone}</p>
    }
  }
]
